import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { ASSIGNMENTS, IDB_ATTRIBUTES } from '../../../components/RxForm/IDB/constants';
import SaveForLater from './SaveForLater';
import SimilarPatientDetection from './SimilarPatientDetection';
import BondingDateReminder from '../IDB/SavingProcess/BondingDateReminder';
import CreateCustomBracketSetRequest from '../IDB/SavingProcess/CreateCustomBracketSetRequest';

const SAVING_PROCESS_STEP_SAVE_FOR_LATER_CONFIRMATION = 1;
const SAVING_PROCESS_STEP_CHECK_DUPLICATES = 2;
const SAVING_PROCESS_STEP_REQUEST_CUSTOM_BRACKET_SET_CREATION = 3;
const SAVING_PROCESS_STEP_SUBMIT = 4;
const SAVING_PROCESS_STEP_BONDING_DATE_REMINDER = 5;

const SAVING_PROCESS_COMPLETED = 6;

const SavingProcess = ({
  values,
  submit,
  finish,
  cancel,
  skipCustomBracketSetCreationRequest,
  skipBondingDateReminder,
  resetCustomBracketSets
}) => {
  const initialData = {
    step: values ? 1 : null,
    submitting: false,
    additionalValues: {},
    additionalActions: {
      clearCustomBracketSetsAfterCaseSubmission: false
    }
  };
  const [data, setData] = useState(initialData);

  const handleCancel = useCallback(() => {
    setData(initialData);
    cancel();
  }, [cancel, setData, initialData]);

  const nextStep = useCallback(
    (newValues = {}, additionalActions = {}) => {
      setData(prev => ({
        ...prev,
        step: prev.step + 1,
        additionalValues: { ...prev.additionalValues, ...newValues },
        additionalActions: { ...prev.additionalActions, ...additionalActions }
      }));
    },
    [setData]
  );

  useEffect(() => {
    if (data.step === SAVING_PROCESS_STEP_SUBMIT && !data.submitting) {
      setData(prev => ({ ...prev, submitting: true }));
      submit(data.additionalValues).then(result => {
        if (result !== false) {
          setData(prev => ({
            ...prev,
            step: prev.step + 1,
            submitting: false
          }));
          if (
            data.additionalActions.clearCustomBracketSetsAfterCaseSubmission &&
            resetCustomBracketSets
          ) {
            resetCustomBracketSets();
          }
        } else {
          handleCancel();
        }
      });
    }
    if (data.step === SAVING_PROCESS_STEP_BONDING_DATE_REMINDER && skipBondingDateReminder) {
      nextStep();
      return;
    }
    if (data.step === SAVING_PROCESS_COMPLETED) {
      setData(initialData);
      finish();
    }
  }, [
    data,
    setData,
    finish,
    values,
    handleCancel,
    submit,
    initialData,
    resetCustomBracketSets,
    skipBondingDateReminder,
    nextStep
  ]);

  return (
    <>
      {data.step === SAVING_PROCESS_STEP_SAVE_FOR_LATER_CONFIRMATION && (
        <SaveForLater
          value={values[IDB_ATTRIBUTES.SAVED_FOR_LATER]}
          proceed={newValues => nextStep(newValues)}
          cancel={handleCancel}
        />
      )}
      {data.step === SAVING_PROCESS_STEP_REQUEST_CUSTOM_BRACKET_SET_CREATION && (
        <CreateCustomBracketSetRequest
          disabled={
            skipCustomBracketSetCreationRequest ||
            values[IDB_ATTRIBUTES.INITIAL_CASE_SETUP_ASSIGNMENT] !== ASSIGNMENTS.ORTHOSELECT
          }
          bracketLibraries={values[IDB_ATTRIBUTES.BRACKET_LIBRARIES]}
          proceed={newValues =>
            nextStep(newValues, { clearCustomBracketSetsAfterCaseSubmission: true })
          }
          cancel={handleCancel}
        />
      )}
      <SimilarPatientDetection
        active={data.step === SAVING_PROCESS_STEP_CHECK_DUPLICATES}
        data={{
          first_name: values.first_name,
          last_name: values.last_name,
          case_number_text: values.case_number_text,
          organization_id: values.organization_id,
          user_id: values.user_id
        }}
        cancel={handleCancel}
        proceed={() => nextStep({})}
      />
      {data.step === SAVING_PROCESS_STEP_BONDING_DATE_REMINDER && !skipBondingDateReminder && (
        <BondingDateReminder proceed={() => nextStep()} />
      )}
    </>
  );
};

SavingProcess.propTypes = {
  skipCustomBracketSetCreationRequest: PropTypes.bool.isRequired,
  skipBondingDateReminder: PropTypes.bool,
  values: PropTypes.shape({
    [IDB_ATTRIBUTES.SAVED_FOR_LATER]: PropTypes.bool,
    [IDB_ATTRIBUTES.BRACKET_LIBRARIES]: PropTypes.arrayOf(PropTypes.shape({})),
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    case_number_text: PropTypes.string,
    organization_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  resetCustomBracketSets: PropTypes.func,
  submit: PropTypes.func.isRequired,
  finish: PropTypes.func.isRequired,
  cancel: PropTypes.func.isRequired
};

SavingProcess.defaultProps = {
  values: null,
  resetCustomBracketSets: null,
  skipBondingDateReminder: false
};

export default SavingProcess;
